import React, { useState, useEffect } from 'react';
import { Modal, Input, Select, Button } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

const { Option } = Select;

const fieldTypeOptions = [
  { label: 'Text',     value: 'text' },
  { label: 'Number',   value: 'Number' },
  { label: 'Phone Number',   value: 'Phone Number' },
  { label: 'Email',    value: 'email' },
  { label: 'Phone',    value: 'phone' },
  { label: 'Date',     value: 'date' },
  { label: 'Textarea', value: 'textarea' },
  { label: 'Checkbox', value: 'checkbox' },
  { label: 'Radio',    value: 'radio' },
  { label: 'Select',   value: 'select' },
  { label: 'File',     value: 'file' },
];

// Convert label to @@UPPERCASE format: "Hospital Name" → "@@HOSPITALNAME"
const toUniqueLabel = (str) => {
  if (!str) return '';
  return '@@' + str
    .trim()
    .toUpperCase()
    .replace(/[\s_\-]+/g, '');
};

// editData shape: { fieldId, nodeId, label, fieldType, formUniqueLabel }

const AddFormFieldModal = ({ visible, onClose, onSubmit, editData, onDelete }) => {
  const [label, setLabel]                   = useState('');
  const [formUniqueLabel, setFormUniqueLabel] = useState('');
  const [fieldType, setFieldType]           = useState(undefined);
  const [fieldOptions, setFieldOptions]     = useState([]);
  const [optionText, setOptionText]         = useState('');
  const [optionValue, setOptionValue]       = useState('');
  const [editingOptionIdx, setEditingOptionIdx] = useState(null);
  const [error, setError]                   = useState({});
  const [loading, setLoading]               = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const isEdit = !!editData;

  useEffect(() => {
    if (visible) {
      setLabel(isEdit ? editData.label : '');
      setFormUniqueLabel(isEdit ? (editData.formUniqueLabel || toUniqueLabel(editData.label)) : '');
      setFieldType(isEdit ? editData.fieldType : undefined);
      setFieldOptions(isEdit && editData.fieldOptions ? (() => {
        try {
          const parsed = typeof editData.fieldOptions === 'string' ? JSON.parse(editData.fieldOptions) : editData.fieldOptions;
          return Array.isArray(parsed) ? parsed : [];
        } catch { return []; }
      })() : []);
      setOptionText('');
      setOptionValue('');
      setEditingOptionIdx(null);
      setError({});
    }
  }, [visible, editData]);

  const handleLabelChange = (value) => {
    setLabel(value);
    setFormUniqueLabel(toUniqueLabel(value));
    if (error.label) setError(p => ({ ...p, label: '' }));
  };

  const handleAddOption = () => {
    if (!optionText.trim()) return;
    const newOption = {
      text: optionText.trim(),
      value: optionValue.trim() || optionText.trim().toLowerCase().replace(/\s+/g, '_'),
      order: editingOptionIdx !== null ? fieldOptions[editingOptionIdx].order : fieldOptions?.length + 1,
    };
    if (editingOptionIdx !== null) {

      // Update existing option
      setFieldOptions(prev => prev?.map((opt, i) => i === editingOptionIdx ? newOption : opt));
      setEditingOptionIdx(null);
    } else {
      // Add new option
      setFieldOptions(prev => [...prev, newOption]);
    }
    setOptionText('');
    setOptionValue('');
  };

  const handleEditOption = (idx) => {
    const opt = fieldOptions[idx];
    setOptionText(opt.text);
    setOptionValue(opt.value);
    setEditingOptionIdx(idx);
  };

  const handleRemoveOption = (idx) => {
    setFieldOptions(prev => prev?.filter((_, i) => i !== idx));
    if (editingOptionIdx === idx) {
      setOptionText('');
      setOptionValue('');
      setEditingOptionIdx(null);
    }
  };

  const handleCancelEdit = () => {
    setOptionText('');
    setOptionValue('');
    setEditingOptionIdx(null);
  };

  const validate = () => {
    const errs = {};
    if (!label.trim()) errs.label = 'Field name is required.';
    if (!fieldType)    errs.fieldType = 'Field type is required.';
    if (fieldType === 'select' && fieldOptions?.length === 0) {
      errs.fieldOptions = 'At least one option is required for select field.';
    }
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs)?.length > 0) { setError(errs); return; }
    setError({});
    setLoading(true);
    try {
      await onSubmit({ 
        label: label.trim(), 
        fieldType, 
        formUniqueLabel: formUniqueLabel.trim() || toUniqueLabel(label.trim()),
        fieldOptions: fieldType === 'select' ? fieldOptions : []
      });
      setLabel('');
      setFormUniqueLabel('');
      setFieldType(undefined);
      setFieldOptions([]);
    } catch {
      setError({ general: 'Failed to save. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: 16, color: '#474747', fontWeight: "bold" }}>
          {isEdit ? 'Edit Form Field' : 'Add Form Field'}
        </span>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      className="ag-modal"
      bodyStyle={{ padding: '20px 28px 28px', borderTop: "1px solid #bfbfbf" }}
    >
      {/* Field Name */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, color: '#333', display: 'block', marginBottom: 6 }}>
          Field Name <span style={{ color: '#720C20' }}>*</span>
        </label>
        <Input
          placeholder="e.g., Hospital Name"
          value={label}
          onChange={(e) => handleLabelChange(e.target.value)}
          onPressEnter={handleSubmit}
          style={{ borderRadius: 2, borderColor: error.label ? '#ff4d4f' : '#720C20', height: 40 }}
        />
        {error.label && <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4, marginBottom: 0 }}>{error.label}</p>}
      </div>

      {/* Form Unique Label */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, color: '#333', display: 'block', marginBottom: 6 }}>
          Unique Label <span style={{ fontSize: 11, color: '#999' }}>(auto-generated, editable)</span>
        </label>
        <Input
          placeholder="e.g., @@HOSPITALNAME"
          value={formUniqueLabel}
          onChange={(e) => setFormUniqueLabel(e.target.value)}
          style={{ borderRadius: 2, borderColor: '#d9d9d9', height: 40 }}
        />
      </div>

      {/* Field Type */}
      <div style={{ marginBottom: 8 }}>
        <label style={{ fontSize: 13, color: '#333', display: 'block', marginBottom: 6 }}>
          Field Type <span style={{ color: '#720C20' }}>*</span>
        </label>
        <Select
          placeholder="Select field type"
          value={fieldType}
          onChange={(val) => { setFieldType(val); if (error.fieldType) setError(p => ({ ...p, fieldType: '' })); }}
          style={{ width: '100%', borderRadius: 2 }}
          size="large"
        >
          {fieldTypeOptions?.map(ft => (
            <Option key={ft.value} value={ft?.value}>{ft?.label}</Option>
          ))}
        </Select>
        {error.fieldType && <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4, marginBottom: 0 }}>{error.fieldType}</p>}
      </div>

      {/* Field Options - Show only when fieldType is 'select' */}
      {fieldType === 'select' && (
        <div style={{ marginTop: 16, marginBottom: 8, border: '1px solid #e8e8e8', borderRadius: 4, padding: 12, backgroundColor: '#fafafa' }}>
          <label style={{ fontSize: 13, color: '#333', fontWeight: 600, display: 'block', marginBottom: 8 }}>
            Options <span style={{ color: '#720C20' }}>*</span>
          </label>
          
          {/* Options List */}
          {fieldOptions?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {fieldOptions?.map((opt, idx) => {
                const isEditing = editingOptionIdx === idx;
                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 10px',
                      marginBottom: 6,
                      backgroundColor: isEditing ? '#fff9e6' : '#fff',
                      border: isEditing ? '1px solid #faad14' : '1px solid #e8e8e8',
                      borderRadius: 4,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>{opt.text}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>Value: {opt.value} | Order: {opt.order}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        size="small"
                        onClick={() => handleEditOption(idx)}
                        style={{ fontSize: 11, padding: '2px 8px' }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        danger
                        onClick={() => handleRemoveOption(idx)}
                        style={{ fontSize: 11, padding: '2px 8px' }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add/Edit Option Form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Input
              placeholder="Option Text (e.g., Volvo)"
              value={optionText}
              onChange={(e) => setOptionText(e.target.value)}
              size="small"
              style={{ borderRadius: 2 }}
            />
            <Input
              placeholder="Option Value (e.g., volvo) - auto-generated if empty"
              value={optionValue}
              onChange={(e) => setOptionValue(e.target.value)}
              size="small"
              style={{ borderRadius: 2 }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                size="small"
                onClick={handleAddOption}
                disabled={!optionText.trim()}
                style={{ backgroundColor: '#720C20', borderColor: '#720C20', color: '#fff', flex: 1 }}
              >
                {editingOptionIdx !== null ? 'Update Option' : 'Add Option'}
              </Button>
              {editingOptionIdx !== null && (
                <Button size="small" onClick={handleCancelEdit}>
                  Cancel
                </Button>
              )}
            </div>
          </div>
          {error.fieldOptions && <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 8, marginBottom: 0 }}>{error.fieldOptions}</p>}
        </div>
      )}

      {error.general && <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{error.general}</p>}

      <div style={{ display: 'flex', justifyContent: isEdit ? 'space-between' : 'flex-end', alignItems: 'center', gap: 10, marginTop: 24 }}>
        {isEdit && onDelete && (
          <Button
            danger
            icon={<DeleteOutlined color="rgb(114, 12, 32)" />}
            onClick={() => setConfirmVisible(true)}
            disabled={loading}
            style={{
              borderRadius: 2,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              borderColor: "rgb(114, 12, 32)",
              color: "rgb(114, 12, 32)"
            }}
          >
            Delete
          </Button>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            style={{ borderColor: '#720C20', color: '#720C20', borderRadius: 2, fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            loading={loading}
            onClick={handleSubmit}
            style={{ backgroundColor: '#720C20', borderColor: '#720C20', color: '#fff', borderRadius: 2, fontWeight: 500 }}
          >
            {isEdit ? 'Update Field' : 'Add Field'}
          </Button>
        </div>
      </div>

      {/* Delete confirmation */}
      <Modal
        visible={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        footer={null}
        centered
        width={420}
        zIndex={1200}
        closable
        className="ag-modal"
        bodyStyle={{ padding: '28px 28px 20px' }}
      >
        <p style={{ fontSize: 15, color: '#333', marginBottom: 20, textAlign: 'center' }}>
          Are you sure you want to delete <strong>{editData?.label}</strong>?
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <Button onClick={() => setConfirmVisible(false)} style={{ minWidth: 100, height: 36, borderRadius: 4, borderColor: '#720C20', color: '#720C20' }}>Cancel</Button>
          <Button
            onClick={() => { setConfirmVisible(false); onDelete(editData.fieldId); }}
            style={{ minWidth: 100, height: 36, borderRadius: 4, backgroundColor: '#720C20', borderColor: '#720C20', color: '#fff' }}
          >Delete</Button>
        </div>
      </Modal>
    </Modal>
  );
};

export default AddFormFieldModal;
